import { CloseOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, theme, Tooltip } from "../ui";
import type { MenuProps } from "../ui";
import React from "react";
import type { AppShellIconKey } from "./appShellIcons";
import { AppShellIcon } from "./AppShellIcon";
import { appShellMetrics, spacing, typography } from "./spacing";

export type AppShellPinnedPage = {
  id: string;
  label: string;
  subtitle?: string | null;
  icon?: AppShellIconKey | null;
  avatar?: { src?: string; initials: string };
  active: boolean;
};

const PIN_CHIP_HEIGHT = appShellMetrics.pinChipHeight;
const PIN_CHIP_MAX_WIDTH = appShellMetrics.pinChipMaxWidth;
const PIN_CHIP_SLOT_WIDTH = PIN_CHIP_MAX_WIDTH + appShellMetrics.pinGap;
const PIN_SEPARATOR_HEIGHT = appShellMetrics.pinSeparatorHeight;
const PIN_CLOSE_BUTTON_SIZE = appShellMetrics.pinCloseButtonSize;
const PIN_CLOSE_BUTTON_OFFSET = appShellMetrics.pinCloseButtonOffset;

/** Presentation and overflow only. Resolution, authorization and persistence stay with the product. */
export type AppShellPinnedPagesProps = {
  items: AppShellPinnedPage[];
  compact: boolean;
  labels: { pinnedPages: string; removePin: string };
  onOpen: (id: string) => void | Promise<void>;
  onRemove: (id: string) => void;
};
export function AppShellPinnedPages({ items: suppliedItems, compact, labels, onOpen, onRemove }: AppShellPinnedPagesProps) {
  const items = suppliedItems.map(entry => ({ ...entry, adornment: entry.avatar ? <Avatar size={appShellMetrics.pinAvatarSize} src={entry.avatar.src} style={{ fontSize: typography.xxs, flexShrink: 0 }}>{entry.avatar.initials}</Avatar> : entry.icon ? <span style={{ fontSize: appShellMetrics.pinIconFontSize, display: "inline-flex" }}><AppShellIcon name={entry.icon} /></span> : undefined }));
  const { token } = theme.useToken();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = React.useState(0);
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);
  React.useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => setAvailableWidth(Math.floor(entries[0]?.contentRect.width ?? 0)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const tooltipTitle = (entry: AppShellPinnedPage) => entry.subtitle ? `${entry.label} · ${entry.subtitle}` : entry.label;
  const capacity = Math.max(0, Math.floor(availableWidth / PIN_CHIP_SLOT_WIDTH));
  const visibleCount = compact ? 0 : items.length <= capacity ? items.length : Math.max(0, capacity - 1);
  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);

  const overflowMenu: MenuProps = {
    items: overflow.map((entry) => ({
      key: entry.id,
      icon: entry.adornment,
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: spacing.tightGap }}>
          <span>{tooltipTitle(entry)}</span>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label={labels.removePin}
            onClick={(e) => {
              e.stopPropagation();
              return onRemove(entry.id);
            }}
          />
        </span>
      )
    })),
    onClick: ({ key, domEvent }) => {
      domEvent.stopPropagation();
      const entry = overflow.find((x) => x.id === String(key));
      if (entry) return onOpen(entry.id);
    }
  };

  // Listensymbol statt Pinnadel: Die Nadel rechts in der Kopfzeile ist die Aktion „Seite anpinnen“.
  // Dasselbe Symbol hier würde zwei verschiedene Dinge bedeuten — hier wird eine Liste geöffnet.
  const overflowTrigger =
    overflow.length > 0 ? (
      <Dropdown menu={overflowMenu} trigger={["click"]} placement="bottomRight">
        <Tooltip title={labels.pinnedPages}>
          <Button
            type="text"
            aria-label={labels.pinnedPages}
            style={{ height: PIN_CHIP_HEIGHT, flexShrink: 0, display: "inline-flex", alignItems: "center", gap: spacing.microGap }}
            icon={compact ? <UnorderedListOutlined /> : undefined}
          >
            {compact ? overflow.length : `+${overflow.length}`}
          </Button>
        </Tooltip>
      </Dropdown>
    ) : null;

  return (
    <div
      ref={containerRef}
      aria-label={labels.pinnedPages}
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: appShellMetrics.pinGap,
        overflow: "hidden",
        marginInlineStart: appShellMetrics.pinMargin
      }}
    >
      {items.length > 0 ? (
        <span
          aria-hidden
          style={{
            width: appShellMetrics.hairline,
            height: PIN_SEPARATOR_HEIGHT,
            background: "var(--mq-frame-pin-separator, var(--mq-theme-line))",
            marginInlineEnd: spacing.elementGap,
            flexShrink: 0
          }}
        />
      ) : null}
      {visible.map((entry) => (
        <div
          key={entry.id}
          // Flex statt Zeilenbox: Ein inline-flex-Chip würde an der Baseline hängen, und die
          // liegt bei einem Foto-Avatar (Bild, keine Textbaseline) anders als bei einem
          // Initialen-Avatar — die Chips säßen dann unterschiedlich hoch in der Leiste.
          style={{ position: "relative", flexShrink: 1, minWidth: 0, display: "flex", alignItems: "center" }}
          onMouseEnter={() => setHoveredId(entry.id)}
          onMouseLeave={() => setHoveredId((prev) => (prev === entry.id ? null : prev))}
        >
          <Tooltip title={tooltipTitle(entry)}>
            <Button
              type="text"
              className="mq-frame-pin"
              aria-label={tooltipTitle(entry)}
              aria-current={entry.active ? "page" : undefined}
              onClick={() => onOpen(entry.id)}
              onFocus={() => setHoveredId(entry.id)}
              style={{
                height: PIN_CHIP_HEIGHT,
                maxWidth: PIN_CHIP_MAX_WIDTH,
                display: "inline-flex",
                alignItems: "center",
                gap: spacing.tightGap,
                paddingInline: spacing.tightGap,
                borderRadius: token.borderRadiusLG
                // Flächen und Schrift (auch aktiv/Hover) aus app-frame.css: auf der Leistenfläche der oberen
                // Zeile in Leistenfarben, mobil auf der Seitenfläche in Theme-Farben.
              }}
            >
              {entry.adornment}
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0
                }}
              >
                {entry.label}
              </span>
            </Button>
          </Tooltip>
          {hoveredId === entry.id ? (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined style={{ fontSize: typography.xxs }} />}
              aria-label={labels.removePin}
              onClick={(e) => {
                e.stopPropagation();
                return onRemove(entry.id);
              }}
              style={{
                position: "absolute",
                top: PIN_CLOSE_BUTTON_OFFSET,
                insetInlineEnd: PIN_CLOSE_BUTTON_OFFSET,
                width: PIN_CLOSE_BUTTON_SIZE,
                height: PIN_CLOSE_BUTTON_SIZE,
                minWidth: PIN_CLOSE_BUTTON_SIZE,
                padding: 0,
                borderRadius: token.borderRadiusSM,
                background: token.colorBgElevated,
                boxShadow: token.boxShadowTertiary
              }}
            />
          ) : null}
        </div>
      ))}
      {overflowTrigger}
    </div>
  );
}
